import type { DomainCode } from "./types";

export const DOMAIN_ORDER: DomainCode[] = ["STR", "REL", "EXE", "INF"];

export interface OverlayRow {
  code: DomainCode;
  /** Bar widths, 0-100: each domain's share of the person's own answers and of the raters'. */
  you: number;
  others: number;
}

/**
 * You and others on one chart. The two come from different scales: your domain counts are
 * picks out of 12, the raters' scores are most-minus-least on 0-100 (50 = neutral, and the
 * four always add up to 200). So both are turned into shares (how much each domain takes up
 * of that side's answers), then scaled so the largest bar is full width. No numbers are
 * shown: only how the two lean, side by side.
 */
export function domainOverlay(
  selfCounts: Partial<Record<DomainCode, number>>,
  otherScores: Partial<Record<DomainCode, number>>,
): OverlayRow[] {
  const share = (values: Partial<Record<DomainCode, number>>) => {
    const total = DOMAIN_ORDER.reduce(
      (n, d) => n + Math.max(0, values[d] ?? 0),
      0,
    );
    return Object.fromEntries(
      DOMAIN_ORDER.map((d) => [
        d,
        total > 0 ? Math.max(0, values[d] ?? 0) / total : 0,
      ]),
    ) as Record<DomainCode, number>;
  };
  const you = share(selfCounts);
  const others = share(otherScores);
  const max =
    Math.max(...DOMAIN_ORDER.flatMap((d) => [you[d], others[d]]), 0) || 1;
  const width = (x: number) => Math.round((x / max) * 100);
  return [...DOMAIN_ORDER]
    .sort(
      (a, b) =>
        you[b] - you[a] ||
        others[b] - others[a] ||
        DOMAIN_ORDER.indexOf(a) - DOMAIN_ORDER.indexOf(b),
    )
    .map((code) => ({
      code,
      you: width(you[code]),
      others: width(others[code]),
    }));
}
